'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { Search, MapPin, Phone, ChevronRight } from 'lucide-react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { createClient } from '@/lib/supabase/client';

interface TrackListing {
  id: string;
  track_name: string;
  track_address: string;
  track_phone: string;
  avatar_url: string | null;
  is_open: boolean;
  bio: string;
}

export default function TracksPage() {
  const supabase = createClient();
  const [tracks, setTracks] = useState<TrackListing[]>([]);
  const [filtered, setFiltered] = useState<TrackListing[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const loadTracks = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('track_profiles')
      .select('id, track_name, track_address, track_phone, avatar_url, is_open, bio')
      .order('track_name', { ascending: true });
    if (data) {
      setTracks(data as TrackListing[]);
      setFiltered(data as TrackListing[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadTracks();
  }, [loadTracks]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) {
      setFiltered(tracks);
      return;
    }
    const q = query.trim().toLowerCase();
    setFiltered(tracks.filter(t =>
      t.track_name?.toLowerCase().includes(q) ||
      t.track_address?.toLowerCase().includes(q)
    ));
  };

  const handleQueryChange = (val: string) => {
    setQuery(val);
    if (!val.trim()) {
      setFiltered(tracks);
    } else {
      const q = val.trim().toLowerCase();
      setFiltered(tracks.filter(t =>
        t.track_name?.toLowerCase().includes(q) ||
        t.track_address?.toLowerCase().includes(q)
      ));
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-1 pt-16">
        <div className="max-w-3xl mx-auto px-4 py-12">
          <div className="mb-8">
            <p className="text-xs font-bold uppercase tracking-widest text-primary mb-2">Directory</p>
            <h1 className="text-4xl md:text-5xl font-extrabold uppercase tracking-tight text-foreground mb-2">Tracks</h1>
            <p className="text-muted-foreground text-sm">Browse all registered karting tracks on SpeedPass. Click a track to view its profile and purchase tickets.</p>
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} className="flex gap-3 mb-8">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={query}
                onChange={e => handleQueryChange(e.target.value)}
                placeholder="Search by track name or location..."
                className="w-full pl-9 pr-4 py-3 rounded-md bg-input border border-border text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-3 text-sm font-bold uppercase tracking-wide rounded-md btn-primary disabled:opacity-60 flex items-center gap-2"
            >
              <Search size={15} />Search
            </button>
          </form>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <span className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-card border border-border rounded-xl p-12 text-center">
              <MapPin size={40} className="text-muted-foreground mx-auto mb-3" />
              <p className="text-sm font-semibold text-muted-foreground">
                {query ? 'No tracks found matching your search.' : 'No tracks registered yet.'}
              </p>
              {query && (
                <button onClick={() => { setQuery(''); setFiltered(tracks); }} className="text-xs text-primary font-semibold hover:underline mt-2">
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-4">
                {filtered.length} Track{filtered.length !== 1 ? 's' : ''} Found
              </p>
              {filtered.map(track => (
                <Link
                  key={track.id}
                  href={`/track/${track.id}`}
                  className="flex items-center gap-4 bg-card border border-border rounded-xl px-5 py-4 hover:border-primary/40 hover:bg-primary/5 transition-all group"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 overflow-hidden">
                    {track.avatar_url ? (
                      <img src={track.avatar_url} alt={track.track_name} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <MapPin size={20} className="text-primary" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-extrabold text-foreground uppercase tracking-wide">{track.track_name}</p>
                    {track.track_address && (
                      <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1 truncate">
                        <MapPin size={10} className="shrink-0" />{track.track_address}
                      </p>
                    )}
                    {track.track_phone && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Phone size={10} className="shrink-0" />{track.track_phone}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${track.is_open ? 'bg-positive/10 text-positive border border-positive/20' : 'bg-negative/10 text-negative border border-negative/20'}`}>
                      {track.is_open ? 'OPEN' : 'CLOSED'}
                    </span>
                    <span className="flex items-center gap-1 text-xs font-semibold text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                      View Track <ChevronRight size={13} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
